import React, { useState } from 'react';
import { useVault } from '../context/VaultContext';

export default function VaultUnlockPanel() {
  const [mode, setMode] = useState('unlock'); // unlock | setup
  const [passphrase, setPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [localError, setLocalError] = useState('');
  const [recoveryKey, setRecoveryKey] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const [unlockMethod, setUnlockMethod] = useState('passphrase');
  const [recoveryKeyInput, setRecoveryKeyInput] = useState('');

  const {
    isUnlocked,
    isUnlocking,
    isSettingUp,
    vaultError,
    unlockVault,
    unlockVaultWithRecoveryKey,
    setupVault,
    lockVault,
  } = useVault();

  const busy = isUnlocking || isSettingUp;

  const preventParentSubmit = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleRecoveryUnlock = async () => {
    setLocalError('');
    setRecoveryKey('');
    setCopyMessage('');

    if (!recoveryKeyInput.trim()) {
      setLocalError('Recovery key is required');
      return;
    }

    const normalizedKey = recoveryKeyInput
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');

    if (!normalizedKey.startsWith('MSDBRCVR')) {
      setLocalError('Invalid recovery key format');
      return;
    }

    if (normalizedKey.length !== 36) {
      setLocalError('Invalid recovery key format');
      return;
    }

    try {
      await unlockVaultWithRecoveryKey({ recoveryKey: normalizedKey });
      setRecoveryKeyInput('');
    } catch (err) {
      console.log('recovery unlock error:', err);

      const status = err?.response?.status;
      const name = err?.name;
      const message = String(err?.message || '');

      if (status === 404) {
        setMode('setup');
        return;
      }

      if (status === 400 || status === 401 || status === 403) {
        setLocalError('Invalid recovery key');
        return;
      }

      if (
        name === 'OperationError' ||
        /unwrap|decrypt|recovery key/i.test(message)
      ) {
        setLocalError('Invalid recovery key');
        return;
      }

      setLocalError(message || 'Failed to unlock vault with recovery key');
    }
  };

  const handleUnlock = async () => {
    setLocalError('');
    setRecoveryKey('');
    setCopyMessage('');

    if (!passphrase.trim()) {
      setLocalError('Passphrase is required');
      return;
    }

    try {
      await unlockVault({ passphrase });
      setPassphrase('');
    } catch (err) {
      console.log('unlock error:', err);

      const status = err?.response?.status;
      const name = err?.name;
      const message = String(err?.message || '');

      if (status === 404) {
        setMode('setup');
        return;
      }

      if (status === 400 || status === 401 || status === 403) {
        setLocalError('Incorrect passphrase');
        return;
      }

      if (
        name === 'OperationError' ||
        /unwrap|decrypt|passphrase/i.test(message)
      ) {
        setLocalError('Incorrect passphrase');
        return;
      }

      setLocalError(message || 'Failed to unlock vault');
    }
  };

  const handleSetup = async () => {
    setLocalError('');
    setRecoveryKey('');
    setCopyMessage('');

    if (!passphrase.trim()) {
      setLocalError('Passphrase is required');
      return;
    }

    if (passphrase !== confirmPassphrase) {
      setLocalError('Passphrases do not match');
      return;
    }

    try {
      const result = await setupVault({ passphrase });

      setRecoveryKey(result?.recoveryKey || '');
      setPassphrase('');
      setConfirmPassphrase('');
    } catch (err) {
      setLocalError(err?.message || 'Failed to set up vault');
    }
  };

  const handleCopyRecoveryKey = async () => {
    try {
      await navigator.clipboard.writeText(recoveryKey);
      setCopyMessage('Recovery key copied');
    } catch (err) {
      console.error('copy recovery key failed', err);
      setCopyMessage('Unable to copy recovery key');
    }
  };

  if (isUnlocked) {
    return (
      <div
        style={{
          marginBottom: 16,
          padding: 12,
          border: '1px solid #dbe4f0',
          borderRadius: 12,
        }}
      >
        <div style={{ marginBottom: 8, fontWeight: 600 }}>Vault unlocked</div>

        <button type="button" onClick={lockVault}>
          Lock Vault
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        marginBottom: 16,
        padding: 12,
        border: '1px solid #dbe4f0',
        borderRadius: 12,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {mode === 'setup' ? 'Set up your vault' : 'Unlock Sensitive Entries'}
      </div>

      <div style={{ marginBottom: 10 }}>
        {mode === 'setup'
          ? 'Create a vault passphrase to protect your sensitive entries.'
          : 'Enter your vault passphrase to view and edit sensitive entries.'}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button
          type="button"
          onClick={() => setUnlockMethod('passphrase')}
          disabled={busy || unlockMethod === 'passphrase'}
        >
          Use Passphrase
        </button>
        <button
          type="button"
          onClick={() => setUnlockMethod('recovery')}
          disabled={busy || unlockMethod === 'recovery'}
        >
          Use Recovery Key
        </button>
      </div>

      {unlockMethod === 'passphrase' ? (
        <input
          type="password"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          onKeyDown={preventParentSubmit}
          placeholder="Vault passphrase"
          style={{ marginBottom: 8, width: '100%' }}
        />
      ) : (
        <input
          type="text"
          value={recoveryKeyInput}
          onChange={(e) => setRecoveryKeyInput(e.target.value.toUpperCase())}
          placeholder="MSDB-RCVR-...."
          style={{ marginBottom: 8, width: '100%' }}
        />
      )}

      {mode === 'setup' && (
        <input
          type="password"
          value={confirmPassphrase}
          onChange={(e) => setConfirmPassphrase(e.target.value)}
          placeholder="Confirm passphrase"
          style={{ marginBottom: 8, width: '100%' }}
        />
      )}

      {mode === 'setup' ? (
        <>
          {!recoveryKey ? (
            <>
              <button type="button" onClick={handleSetup} disabled={busy}>
                {isSettingUp ? 'Creating...' : 'Create Vault'}
              </button>
              <button
                type="button"
                onClick={() => setMode('unlock')}
                style={{ marginLeft: 8 }}
              >
                Back
              </button>
            </>
          ) : (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                border: '1px solid #facc15',
                borderRadius: 8,
                background: '#fef9c3',
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                ⚠️ Save your recovery key
              </div>

              <div
                style={{
                  fontFamily: 'monospace',
                  fontSize: 14,
                  marginBottom: 10,
                  wordBreak: 'break-word',
                }}
              >
                {recoveryKey}
              </div>

              <button type="button" onClick={handleCopyRecoveryKey}>
                Copy Recovery Key
              </button>

              {copyMessage ? (
                <div style={{ marginTop: 8, fontSize: 13 }}>{copyMessage}</div>
              ) : null}

              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => {
                    setRecoveryKey('');
                    setCopyMessage('');
                    setMode('unlock');
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {unlockMethod === 'passphrase' ? (
            <button type="button" onClick={handleUnlock} disabled={busy}>
              {isUnlocking ? 'Unlocking...' : 'Unlock Vault'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleRecoveryUnlock}
              disabled={busy}
            >
              {isUnlocking ? 'Unlocking...' : 'Unlock With Recovery Key'}
            </button>
          )}
        </>
      )}

      {localError && (
        <div style={{ color: 'red', marginTop: 10 }}>{localError}</div>
      )}
      {!localError && vaultError && (
        <div style={{ color: 'red', marginTop: 10 }}>{vaultError}</div>
      )}
    </div>
  );
}
